import ClipperLib from 'clipper-lib'
import SvgPath from 'svgpath'
import { iconStyle } from './iconStyle'

/**
 * Stroke width for line icons. Line icons are drawn with 2px strokes and stored as filled
 * outlines, so another width is the same outline grown or shrunk by half the difference
 * on each side (round joins, like a stroke's). Solid and duotone icons keep their drawing.
 */

export const STROKE = { min: 1, max: 3, step: 0.25, base: 2 }

// Off until the line icons are redrawn with uniform 2px strokes: the original drawings
// mix weights, so growing or shrinking their outlines would distort them
export const STROKE_WIDTH_ENABLED = false

export const hasStrokeWidth = icon => STROKE_WIDTH_ENABLED && !!icon && iconStyle(icon) === 'line'

export const clampStroke = width => {
	const value = Number(width)
	if (!isFinite(value)) return STROKE.base
	return Math.min(STROKE.max, Math.max(STROKE.min, Math.round(value / STROKE.step) * STROKE.step))
}

const SCALE = 1000 // Clipper works in integers: 0.001px precision
const CURVE_STEPS = 12

// Flatten a path into closed polygons (Clipper points), sampling any curves
function toPolygons(d) {
	const polygons = []
	let current = null
	let x = 0, y = 0, startX = 0, startY = 0
	const point = (px, py) => ({ X: Math.round(px * SCALE), Y: Math.round(py * SCALE) })
	const lineTo = (nx, ny) => {
		if (!current) {
			current = [point(startX, startY)]
			polygons.push(current)
		}
		current.push(point(nx, ny))
		x = nx
		y = ny
	}
	new SvgPath(d).abs().unarc().unshort().iterate(seg => {
		const cmd = seg[0]
		if (cmd === 'M') {
			current = [point(seg[1], seg[2])]
			polygons.push(current)
			x = startX = seg[1]
			y = startY = seg[2]
		} else if (cmd === 'L') lineTo(seg[1], seg[2])
		else if (cmd === 'H') lineTo(seg[1], y)
		else if (cmd === 'V') lineTo(x, seg[1])
		else if (cmd === 'C' || cmd === 'Q') {
			const x0 = x, y0 = y
			for (let i = 1; i <= CURVE_STEPS; i++) {
				const t = i / CURVE_STEPS, u = 1 - t
				if (cmd === 'C') {
					lineTo(
						u * u * u * x0 + 3 * u * u * t * seg[1] + 3 * u * t * t * seg[3] + t * t * t * seg[5],
						u * u * u * y0 + 3 * u * u * t * seg[2] + 3 * u * t * t * seg[4] + t * t * t * seg[6]
					)
				} else {
					lineTo(u * u * x0 + 2 * u * t * seg[1] + t * t * seg[3], u * u * y0 + 2 * u * t * seg[2] + t * t * seg[4])
				}
			}
		} else if (cmd === 'Z' || cmd === 'z') {
			x = startX
			y = startY
			current = null
		}
	})
	return polygons.filter(polygon => polygon.length > 2)
}

const cache = {}
const CACHE_LIMIT = 200
const cached = []

/**
 * A line icon's 24-grid path redrawn at another stroke width
 * @param  {String} d - Path on the 24 grid, drawn at 2px
 * @param  {Number} width - Stroke width in px (1-3)
 * @return {String}
 */
export function restrokePath(d, width) {
	const delta = (clampStroke(width) - STROKE.base) / 2
	if (!delta || !d) return d
	const key = width + '|' + d
	if (cache[key]) return cache[key]

	// Union first so overlapping pieces grow as one shape, as the font fills them
	const clipper = new ClipperLib.Clipper()
	clipper.AddPaths(toPolygons(d), ClipperLib.PolyType.ptSubject, true)
	const shape = new ClipperLib.Paths()
	const nonZero = ClipperLib.PolyFillType.pftNonZero
	clipper.Execute(ClipperLib.ClipType.ctUnion, shape, nonZero, nonZero)

	const offset = new ClipperLib.ClipperOffset(2, 0.01 * SCALE)
	offset.AddPaths(shape, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etClosedPolygon)
	const out = new ClipperLib.Paths()
	offset.Execute(out, delta * SCALE)
	ClipperLib.JS.Lighten(out, 0.005 * SCALE)

	const result = new SvgPath(
		out.map(p => 'M' + p.map(pt => `${pt.X / SCALE} ${pt.Y / SCALE}`).join('L') + 'Z').join('')
	)
		.rel()
		.round(2)
		.toString()

	cache[key] = result
	cached.push(key)
	if (cached.length > CACHE_LIMIT) delete cache[cached.shift()]
	return result
}
